import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * Drag-to-resize width for a left-side panel, persisted to localStorage.
 *
 * Returns the current width plus a pointer-down handler to attach to a drag
 * handle pinned to the panel's right edge. Width is clamped to [min, max].
 *
 * @param {Object} options
 * @param {string} options.storageKey  localStorage key used to persist the width
 * @param {number} options.defaultWidth  initial width when nothing is stored
 * @param {number} [options.min=280]  minimum allowed width
 * @param {number} [options.max=720]  maximum allowed width
 * @returns {{width: number, isResizing: boolean, onResizeStart: (event: React.PointerEvent) => void, resetWidth: () => void}}
 */
const useResizablePanelWidth = ({storageKey, defaultWidth, min = 280, max = 720}) => {
    const clamp = useCallback((value) => Math.min(max, Math.max(min, value)), [max, min]);

    const [width, setWidth] = useState(() => {
        if (typeof window === 'undefined') {
            return defaultWidth;
        }
        const stored = Number(window.localStorage.getItem(storageKey));
        return Number.isFinite(stored) && stored > 0 ? clamp(stored) : defaultWidth;
    });
    const [isResizing, setIsResizing] = useState(false);

    const dragStateRef = useRef({startX: 0, startWidth: 0});

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem(storageKey, String(Math.round(width)));
    }, [storageKey, width]);

    const onResizeStart = useCallback((event) => {
        event.preventDefault();
        dragStateRef.current = {startX: event.clientX, startWidth: width};
        setIsResizing(true);

        const handleMove = (moveEvent) => {
            const delta = moveEvent.clientX - dragStateRef.current.startX;
            setWidth(clamp(dragStateRef.current.startWidth + delta));
        };

        const handleUp = () => {
            setIsResizing(false);
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'col-resize';
        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
    }, [clamp, width]);

    const resetWidth = useCallback(() => {
        setWidth(defaultWidth);
    }, [defaultWidth]);

    return {width, isResizing, onResizeStart, resetWidth};
};

export default useResizablePanelWidth;
